import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import type { Run, CEESystem } from '@/lib/types';
import { KPI_PALETTE } from '@/lib/palette';
import {
  Badge, Button, Card, DataTable, Icon, LED, Sparkline, StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import { Link } from 'react-router-dom';

/**
 * Dashboard home — ambient summary for operators.
 *
 * Layout :
 *   1. Status row (systems online, active runs, timestamp)
 *   2. Six KPI tiles (tests catalogued / suites / catégories / runs
 *      archivés / campagnes 24 h / taux de réussite 7 j)
 *   3. Two-column : [ latest campaign detail | systems + activity sparkline ]
 *   4. Recent runs table
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const [replayFor, setReplayFor] = useState<Run | null>(null);

  const { data: systems = [] } = useQuery({
    queryKey: ['systems'],
    queryFn: () => api.systems(),
    staleTime: 60_000,
  });

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', { limit: 100 }],
    queryFn: () => api.listRuns({ limit: 100 }),
    refetchInterval: 10_000,
  });

  const { data: suites = [] } = useQuery({
    queryKey: ['suites'],
    queryFn: () => api.suites(),
    staleTime: 5 * 60_000,
  });

  const { data: tcParams = [] } = useQuery({
    queryKey: ['params'],
    queryFn: () => api.tcParams(),
    staleTime: 5 * 60_000,
  });

  const { data: userSuites = [] } = useQuery({
    queryKey: ['user-suites'],
    queryFn: () => api.userSuites(),
    staleTime: 30_000,
  });

  const running = runs.filter((r) => r.status === 'running');
  const last7d  = runs.filter((r) => r.started_at && Date.now() - new Date(r.started_at).getTime() < 7 * 86_400_000);
  const completed7d = last7d.filter((r) => r.status === 'completed' || r.status === 'failed');
  const passed7d    = last7d.filter((r) => r.status === 'completed').length;
  const passRate    = completed7d.length > 0 ? (passed7d / completed7d.length) * 100 : 0;

  const latest = useMemo(() => {
    const sorted = [...runs].sort((a, b) => {
      const at = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bt = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bt - at;
    });
    return sorted[0];
  }, [runs]);

  // Source de vérité unique = /api/suites (auto-discovery des .robot
  // qui peuvent réellement tourner). On ne compte PAS depuis /api/params
  // qui n'a que les TC ayant des paramètres configurables (sous-ensemble).
  const tcCount         = suites.reduce((n, s) => n + s.tc_count, 0);
  const suiteCount      = suites.length;
  // Tests avec paramètres personnalisables (sous-ensemble du catalogue).
  const configurableTcs = tcParams.filter((g) => g.tc_id !== 'GLOBAL').length;

  return (
    <div className="content">
      {/* Status row */}
      <div className="h-row" style={{ gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <Badge kind="nt">
          <LED kind={systems.length > 0 ? 'ok' : 'idle'} />
          {systems.length} système{systems.length > 1 ? 's' : ''} enregistré{systems.length > 1 ? 's' : ''}
        </Badge>
        {running.length > 0 && (
          <Badge kind="run">{running.length} campagne{running.length > 1 ? 's' : ''} en cours</Badge>
        )}
        <span className="mono" style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          {new Date().toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })} ·{' '}
          {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* 6 KPI tiles — use the dedicated KPI_PALETTE whose hues are
       * spaced by ≥ 40° so adjacent tiles never feel similar. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <ColoredMetric label="Systèmes"       value={systems.length}    hint="enregistrés"                                    color={KPI_PALETTE[0]} /* sky */ />
        <ColoredMetric label="Tests"          value={tcCount}           hint={`${suiteCount} catégories`}                     color={KPI_PALETTE[1]} /* sun */ />
        <ColoredMetric label="Configurables"  value={configurableTcs}   hint={`sur ${tcCount} tests`}                         color={KPI_PALETTE[2]} /* aqua */ />
        <ColoredMetric label="Suites perso"   value={userSuites.length} hint="créées par l'opérateur"                         color={KPI_PALETTE[5]} /* magenta */ />
        <ColoredMetric label="Campagnes"      value={runs.length}       hint={running.length > 0 ? `${running.length} en cours` : 'archivées'} color={KPI_PALETTE[3]} /* orchid */ />
        <ColoredMetric label="Réussite · 7 j" value={`${passRate.toFixed(0)} %`} hint={`${completed7d.length} campagnes`}     color={KPI_PALETTE[4]} /* slate */ />
      </div>

      {/* Latest campaign + systems */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14, marginBottom: 14 }}>
        <LatestCampaignCard
          latest={latest}
          systems={systems}
          onReplayRequest={(r) => setReplayFor(r)}
        />
        <SystemsCard systems={systems} runs={last7d} />
      </div>

      {/* Recent campagnes — uses the unified DataTable (see CLAUDE.md §0.2) */}
      <RecentCampagnesTable runs={runs} onOpen={(r) =>
        navigate(
          r.status === 'running' || r.status === 'stopping'
            ? `/live?run=${r.run_id}`
            : `/results/${r.run_id}`
        )
      } />

      {replayFor && (
        <ReplayModal
          run={replayFor}
          systems={systems}
          onClose={() => setReplayFor(null)}
          onConfirm={(systemId) => {
            setReplayFor(null);
            // Navigate to /run prefilled with the chosen system + source run.
            navigate(`/run?replay=${replayFor.run_id}&system=${systemId}`);
          }}
        />
      )}
    </div>
  );
}

/* ─── Coloured KPI metric ─────────────────────────────────────────────── */

function ColoredMetric({
  label, value, hint, color,
}: { label: string; value: React.ReactNode; hint?: string; color: { fg: string; bg: string; line: string } }) {
  return (
    <div
      className="metric"
      style={{
        borderLeft: `3px solid ${color.line}`,
        background: `linear-gradient(180deg, ${color.bg}, transparent 80%), var(--surface-1)`,
      }}
    >
      <div className="k" style={{ color: color.fg }}>{label}</div>
      <div className="v">{value}</div>
      {hint && <div className="d"><span>{hint}</span></div>}
    </div>
  );
}

/* ─── Latest campaign card — the focal point of the dashboard ─────────── */

function LatestCampaignCard({
  latest,
  systems,
  onReplayRequest,
}: {
  latest: Run | undefined;
  systems: CEESystem[];
  onReplayRequest: (r: Run) => void;
}) {
  const navigate = useNavigate();

  if (!latest) {
    return (
      <Card title="Dernière campagne" padded>
        <div className="grid-bg" style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', marginBottom: 8 }}>
            ∅ AUCUNE CAMPAGNE
          </div>
          <p style={{ fontSize: 13, margin: 0 }}>
            Lancez votre première campagne via le bouton <b style={{ color: 'var(--text-primary)' }}>Lancer</b>.
          </p>
        </div>
      </Card>
    );
  }

  const total = latest.total ?? 0;
  const pass  = latest.passed ?? 0;
  const fail  = latest.failed ?? 0;
  const skip  = latest.skipped ?? 0;
  const dur   = latest.duration_ms != null ? formatDuration(latest.duration_ms) : '—';

  return (
    <Card
      title="Dernière campagne"
      subtitle={latest.run_id.slice(-8)}
      actions={<StatusBadge status={latest.status} passed={latest.passed} total={latest.total} />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {/* Key facts */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
          <Fact label="Système" value={<span className="mono">{latest.system}</span>} />
          <Fact label="Durée"   value={<span className="mono">{dur}</span>} />
          <Fact label="Lancée"  value={<span className="mono">{latest.started_at ? timeAgo(latest.started_at) : '—'}</span>} />
          <Fact label="Mode"    value={<span>{latest.mode === 'real' ? 'Réel' : 'Simulation'}</span>} />
        </div>

        {/* Stacked pass/fail/skip bar */}
        {total > 0 && (
          <div>
            <div className="label" style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
              <span>Résultat</span>
              <span className="sp" />
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {pass}/{total} tests passés
              </span>
            </div>
            <div style={{
              display: 'flex',
              height: 8,
              background: 'var(--surface-2)',
              borderRadius: 999,
              overflow: 'hidden',
            }}>
              <div style={{ width: `${(pass / total) * 100}%`, background: 'var(--success)' }} />
              <div style={{ width: `${(fail / total) * 100}%`, background: 'var(--danger)' }} />
              <div style={{ width: `${(skip / total) * 100}%`, background: 'var(--warning)' }} />
            </div>
            <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}><LED kind="ok"   style={{ marginRight: 6 }} />{pass} pass</span>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}><LED kind="fail" style={{ marginRight: 6 }} />{fail} fail</span>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}><LED kind="warn" style={{ marginRight: 6 }} />{skip} skip</span>
            </div>
          </div>
        )}

        {/* Actions — bottom right */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          paddingTop: 6,
          borderTop: '1px solid var(--border-subtle)',
          marginTop: 4,
        }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(
              latest.status === 'running' || latest.status === 'stopping'
                ? `/live?run=${latest.run_id}`
                : `/results/${latest.run_id}`
            )}
            leftIcon={<Icon name="external" size={12} />}
          >
            Voir les détails
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onReplayRequest(latest)}
            leftIcon={<Icon name="retry" size={12} />}
            disabled={systems.length === 0}
          >
            Relancer
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span className="label" style={{ margin: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

/* ─── Systems card — right column of the 2-column grid ───────────────── */

function SystemsCard({ systems, runs }: { systems: CEESystem[]; runs: Run[] }) {
  const activity = activitySparkline(runs);
  return (
    <Card title="Systèmes" subtitle={`${systems.length} enregistrés`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {systems.map((s) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
            <div style={{
              width: 28, height: 28,
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface-2)',
              border: '1px solid var(--border-subtle)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-muted)',
              flexShrink: 0,
            }}>
              <Icon name="server" size={14} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                {s.id}
              </span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {s.host}{s.ssh_port ? ` · port ${s.ssh_port}` : ''}
              </span>
            </div>
            <LED kind="ok" />
          </div>
        ))}

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
          <div className="label" style={{ marginBottom: 6 }}>Activité · 7 j</div>
          <Sparkline points={activity.length < 2 ? [0, 0] : activity} w={280} h={36} />
        </div>
      </div>
    </Card>
  );
}

/* ─── Replay modal — choose target system then launch ────────────────── */

function ReplayModal({
  run, systems, onClose, onConfirm,
}: {
  run: Run;
  systems: CEESystem[];
  onClose: () => void;
  onConfirm: (systemId: string) => void;
}) {
  const [selected, setSelected] = useState<string>(run.system ?? systems[0]?.id ?? '');

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(7, 15, 30, 0.82)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--elev-4)',
          width: 480, maxWidth: '92vw',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <header style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Icon name="retry" size={16} />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Relancer la campagne
          </h3>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
            {run.run_id.slice(-8)}
          </span>
          <div className="sp" />
          <button className="btn ghost sm" onClick={onClose} aria-label="Fermer">
            <Icon name="x" size={14} />
          </button>
        </header>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            Sur quel système souhaitez-vous relancer cette campagne ?
          </p>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>Système cible</div>
            <select
              className="input mono"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{ width: '100%' }}
            >
              {systems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id} — {s.host}
                </option>
              ))}
            </select>
          </div>
          <div style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 10, fontSize: 12, color: 'var(--text-muted)',
          }}>
            Source : <span className="mono" style={{ color: 'var(--text-secondary)' }}>
              {run.system}
            </span>
            {selected && selected !== run.system && (
              <>
                {' '}→ cible : <span className="mono" style={{ color: 'var(--text-primary)' }}>{selected}</span>
              </>
            )}
          </div>
        </div>

        <footer style={{
          padding: '10px 18px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onConfirm(selected)}
            disabled={!selected}
            leftIcon={<Icon name="play" size={12} />}
          >
            Relancer
          </Button>
        </footer>
      </div>
    </div>
  );
}

/* ─── Recent campagnes — the unified DataTable card ──────────────────── */

function RecentCampagnesTable({ runs, onOpen }: { runs: Run[]; onOpen: (r: Run) => void }) {
  const rows = useMemo(() => {
    return [...runs]
      .sort((a, b) => {
        const at = a.started_at ? new Date(a.started_at).getTime() : 0;
        const bt = b.started_at ? new Date(b.started_at).getTime() : 0;
        return bt - at;
      })
      .slice(0, 10);
  }, [runs]);

  const pass    = runs.filter((r) => r.status === 'completed').length;
  const fail    = runs.filter((r) => r.status === 'failed').length;
  const running = runs.filter((r) => r.status === 'running').length;
  const closed  = pass + fail;
  const rate    = closed > 0 ? Math.round((pass / closed) * 100) : 0;

  const columns: DataTableColumn<Run>[] = [
    {
      key: 'run_id',
      label: 'Campagne',
      accessor: (r) => <span className="mono" style={{ color: 'var(--text-primary)' }}>{r.run_id.slice(-8)}</span>,
      sortable: true,
      sortValue: (r) => r.run_id,
      width: 'minmax(120px, 1fr)',
      priority: 1,
    },
    {
      key: 'system',
      label: 'Système',
      accessor: (r) => <span className="mono">{r.system}</span>,
      sortable: true,
      sortValue: (r) => r.system ?? '',
      width: 'minmax(130px, 1.2fr)',
      priority: 1,
    },
    {
      key: 'status',
      label: 'Statut',
      accessor: (r) => <StatusBadge status={r.status} passed={r.passed} total={r.total} />,
      sortable: true,
      sortValue: (r) => r.status ?? '',
      width: 'minmax(130px, 0.8fr)',
      priority: 1,
    },
    {
      key: 'tests',
      label: 'Tests',
      accessor: (r) => r.passed != null && r.total != null ? `${r.passed}/${r.total}` : '—',
      sortable: true,
      sortValue: (r) => r.total ?? 0,
      mono: true,
      width: 'minmax(80px, 0.6fr)',
      priority: 2,
      align: 'right',
    },
    {
      key: 'duration',
      label: 'Durée',
      accessor: (r) => r.duration_ms != null ? formatDuration(r.duration_ms) : '—',
      sortable: true,
      sortValue: (r) => r.duration_ms ?? 0,
      mono: true,
      width: 'minmax(80px, 0.6fr)',
      priority: 2,
      align: 'right',
    },
    {
      key: 'started',
      label: 'Quand',
      accessor: (r) => <span className="muted mono">{r.started_at ? timeAgo(r.started_at) : '—'}</span>,
      sortable: true,
      sortValue: (r) => r.started_at ? new Date(r.started_at) : null,
      width: 'minmax(110px, 0.8fr)',
      priority: 3,
      align: 'right',
    },
  ];

  return (
    <DataTable<Run>
      title="Campagnes récentes"
      subtitle={`${rows.length} affichées · ${runs.length} au total`}
      data={rows}
      columns={columns}
      rowKey={(r) => r.run_id}
      onRowClick={onOpen}
      initialSort={{ key: 'started', direction: 'desc' }}
      empty={(
        <>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', marginBottom: 8 }}>
            ∅ AUCUNE CAMPAGNE
          </div>
          <p style={{ fontSize: 13, margin: 0 }}>
            Lancez votre première campagne via le bouton <b style={{ color: 'var(--text-primary)' }}>Lancer</b>.
          </p>
        </>
      )}
      footerLeading={
        <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge kind="nt">{runs.length} au total</Badge>
          {pass    > 0 && <Badge kind="ok">{pass} réussies</Badge>}
          {fail    > 0 && <Badge kind="ko">{fail} échouées</Badge>}
          {running > 0 && <Badge kind="run">{running} en cours</Badge>}
          {closed > 0 && (
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · taux {rate}%
            </span>
          )}
        </div>
      }
      footerTrailing={
        <Link to="/history" className="btn ghost sm" style={{ textDecoration: 'none' }}>
          Voir toutes les campagnes
          <Icon name="external" size={12} />
        </Link>
      }
    />
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rs).padStart(2, '0')}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1)  return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.round(h / 24);
  return `il y a ${d} j`;
}

function activitySparkline(runs: Run[]): number[] {
  const buckets = new Array(7).fill(0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  for (const r of runs) {
    if (!r.started_at) continue;
    const d = new Date(r.started_at); d.setHours(0, 0, 0, 0);
    const idx = 6 - Math.round((now.getTime() - d.getTime()) / 86_400_000);
    if (idx >= 0 && idx < 7) buckets[idx] += 1;
  }
  return buckets;
}
